import React, { useCallback, useRef } from "react";

import { Button, classnames, Icon, PlainButton } from "@culturehq/components";

import { useDocumentEvent, useDoor, useGaTracking, useInsideIframe } from "app/hooks";
import { containedBy, isDeloitte, isMicrosoft, isProgressive, isSynopsys, isTarget, t, Trans } from "app/utils";

import CopyLink from "components/admin/settings/CopyLink";

import styles from "./share.module.scss";

interface ShareInLinkedinProps {
  link: string;
  onClose: () => void;
  onTrack?: (socialMedia: string, storyId?: string, organizationId?: string) => void;
  organizationId?: string;
  storyId?: string;
}

const ShareInLinkedin: React.FC<ShareInLinkedinProps> = ({
  link,
  onClose,
  onTrack,
  organizationId,
  storyId
}) => (
  <Button
    onClick={() => {
      // Tracking del evento
      if (onTrack) {
        onTrack("LinkedIn", storyId, organizationId);
      }

      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}`, "", "_blank, width=500, height=500, resizable=yes, scrollbars=yes");
      if (onClose) {
        onClose();
      }
    }}
  >
    <Icon icon="linkedin" /><Trans>LinkedIn</Trans>
  </Button>
);

interface ShareInFacebookProps {
  link: string;
  onClose: () => void;
  onTrack?: (socialMedia: string, storyId?: string, organizationId?: string) => void;
  organizationId?: string;
  storyId?: string;
}

const ShareInFacebook: React.FC<ShareInFacebookProps> = ({
  link,
  onClose,
  onTrack,
  organizationId,
  storyId
}) => (
  <Button
    onClick={() => {
      // Tracking del evento
      if (onTrack) {
        onTrack("Facebook", storyId, organizationId);
      }

      window.open(`https://www.facebook.com/sharer.php?u=${encodeURIComponent(link)}`, "", "_blank, width=500, height=500, resizable=yes, scrollbars=yes");
      if (onClose) {
        onClose();
      }
    }}
  >
    <Icon icon="facebook" /><Trans>Facebook</Trans>
  </Button>
);

interface ShareInTwitterProps {
  link: string;
  onClose: () => void;
  onTrack?: (socialMedia: string, storyId?: string, organizationId?: string) => void;
  organizationId?: string;
  storyId?: string;
}

const ShareInTwitter: React.FC<ShareInTwitterProps> = ({
  link,
  onClose,
  onTrack,
  organizationId,
  storyId
}) => (
  <Button
    onClick={() => {
      // Tracking
      if (onTrack) {
        onTrack("X", storyId, organizationId);
      }

      window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(link)}`, "", "_blank, width=500, height=500, resizable=yes, scrollbars=yes");
      if (onClose) {
        onClose();
      }
    }}
  >
    <Icon icon="twitter" /><Trans>Twitter</Trans>
  </Button>
);

interface EmbedOptionsProps {
  onClose: () => void;
  orgName: string;
  link: string;
  onTrack?: (socialMedia: string, storyId?: string, organizationId?: string) => void;
  organizationId?: string;
  storyId?: string;
}

const EmbedOptions: React.FC<EmbedOptionsProps> = ({
  onClose,
  orgName,
  link,
  onTrack,
  organizationId,
  storyId
}) => (
  <ul className="dropdown-menu">
    <li>
      <ShareInLinkedin
        link={link}
        onClose={onClose}
        onTrack={onTrack}
        organizationId={organizationId}
        storyId={storyId}
      />
    </li>
    <li>
      <ShareInFacebook
        link={link}
        onClose={onClose}
        onTrack={onTrack}
        organizationId={organizationId}
        storyId={storyId}
      />
    </li>
    <li>
      <ShareInTwitter
        link={link}
        onClose={onClose}
        onTrack={onTrack}
        organizationId={organizationId}
        storyId={storyId}
      />
    </li>
    <li>
      <CopyLink
        link={link}
        orgName={orgName}
        ShareButtonRenderer={undefined}
      />
    </li>
  </ul>
);

interface ShareLinkButtonProps {
  className?: string;
  children: React.ReactNode;
  onClose: () => void;
  open: boolean;
  onToggle: () => void;
  closeOnOutsideClick?: boolean;
  nextToMenu?: boolean;
  modal?: boolean;
  organizationId?: string;
  orgName: string;
}

const ShareLinkButton: React.FC<ShareLinkButtonProps> = ({
  className,
  children,
  onClose,
  open,
  onToggle,
  closeOnOutsideClick,
  nextToMenu,
  modal,
  organizationId,
  orgName
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const insideIframe = useInsideIframe();
  const { trackEvent } = useGaTracking();
  const isTargetOrg = isTarget(orgName);

  useDocumentEvent("click", useCallback(
    event => {
      if (!containedBy(event.target as Node, "chq-mdl") && containerRef.current
        && !containerRef.current.contains(event.target as Node) && closeOnOutsideClick) {
        onClose();
      }
    },
    [closeOnOutsideClick, onClose]
  ));

  const trackClick = () => {
    trackEvent({
      organizationId,
      eventAction: "click_search_careers",
      url: window.location.href,
      type: "culture_page"
    });
  };

  const classList = classnames(
    "QuickOptions",
    "QuickOptions--Actions",
    "ShareQuickOptions",
    styles.quick,
    className && styles[className],
    nextToMenu && styles["menu-next"],
    open && styles["quick-open"]
  );

  if (!modal && (isDeloitte(orgName) || isMicrosoft(orgName) || isSynopsys(orgName)
    || isProgressive(orgName))) {
    const careersMap: Record<string, { url: string; text: string }> = {
      deloitte: {
        url: "https://www2.deloitte.com/us/en/careers/careers.html",
        text: "Explore careers"
      },
      microsoft: {
        url: "https://careers.microsoft.com/v2/global/en/home.html",
        text: "Search Careers"
      },
      synopsys: {
        url: "https://careers.synopsys.com/search-jobs",
        text: "Search Careers"
      },
      progressive: {
        url: "https://careers.progressive.com/search/jobs/?campaign_id=ictugnfUnNU04PLDCVrI5w",
        text: "View Job Openings"
      }
    };

    const orgNameLower = orgName.toLowerCase();
    const careerInfo = careersMap[orgNameLower];
    const { url: careersUrl, text: buttonText } = careerInfo || {};

    if (careersUrl) {
      return (
        <a href={careersUrl} target="_blank" rel="noopener noreferrer" onClick={trackClick}>
          <div ref={containerRef} className={classList}>
            <PlainButton className="Gray-Button" onClick={onToggle}>
              <Trans>{buttonText}</Trans>
            </PlainButton>
          </div>
        </a>
      );
    }
  }

  if (insideIframe) {
    return <></>;
  }

  return (
    <div ref={containerRef} className={classList}>
      {modal ? (
        <PlainButton className="Share-btn" aria-label={t("Share")} onClick={onToggle}>
          {!isTargetOrg && <Icon icon="share" />}
        </PlainButton>
      ) : (
        <Button className="Gray-Button" onClick={onToggle}>
          {!isTargetOrg && <Icon icon="share" />}<Trans>Share</Trans>
        </Button>
      )}
      {open && (
        <div className={classnames("QuickOptions__menu-wrapper", styles["quick--wrap"])}>
          <div className={classnames("QuickOptions__menu box-shadow", styles["quick--menu"])}>
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

interface ShareLinkComponentProps {
  className?: string;
  closeOnOutsideClick?: boolean;
  nextToMenu?: boolean;
  link: string;
  orgName: string;
  organizationId?: string;
  modal?: boolean;
  storyId?: string;
}

const ShareLinkComponent: React.FC<ShareLinkComponentProps> = ({
  className = "",
  closeOnOutsideClick = false,
  nextToMenu = false,
  link,
  orgName,
  organizationId,
  modal = false,
  storyId
}) => {
  const { open, onClose, onToggle } = useDoor(false);
  const { trackEvent } = useGaTracking();

  const handleTrackShare = (
    socialMedia: string
  ) => {
    trackEvent({
      organizationId,
      eventAction: "click_share",
      url: window.location.href,
      type: "culture_page",
      socialMedia,
      ...(storyId && { storyId })
    });
  };

  return (
    <ShareLinkButton
      open={open}
      onClose={onClose}
      onToggle={onToggle}
      className={className}
      closeOnOutsideClick={closeOnOutsideClick}
      nextToMenu={nextToMenu}
      modal={modal}
      organizationId={organizationId}
      orgName={orgName}
    >
      <EmbedOptions
        onClose={onClose}
        link={link}
        orgName={orgName}
        onTrack={handleTrackShare}
        organizationId={organizationId}
        storyId={storyId}
      />
    </ShareLinkButton>
  );
};

export default ShareLinkComponent;
